import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { useEffect, useState } from 'react'

import { useGetQuotingProspects } from 'utils/web_api'
import type { Pagination, QuotingProspect } from 'utils/web_api.schemas'

import RequestAQuoteButton from './RequestAQuoteButton'
import { getLocaleDateString } from '../../utils'
import TanstackTable, {
  DEFAULT_PAGE_SIZE,
} from '../reusable/table/TanstackTable'

const columnHelper = createColumnHelper<QuotingProspect>()
const columns = [
  columnHelper.accessor('name', {
    header: 'Name',
  }),
  columnHelper.accessor('email', {
    header: 'Email',
  }),
  columnHelper.accessor('created_at', {
    header: 'Date created',
    cell: (info) =>
      info.getValue()
        ? getLocaleDateString(new Date(info.getValue()))
        : undefined,
  }),
]

type PartnersQuotesListProps = {
  currentUserId?: string;
  currentPartnerId?: string;
  canOwnProspects?: boolean;
  pageParam?: number | null;
}

export function PartnersQuotesListImpl ({
  currentUserId,
  currentPartnerId,
  canOwnProspects,
  pageParam,
}: PartnersQuotesListProps) {
  const [paginationMetadata, setPaginationMetadata] = useState<
    Pagination | undefined
  >()
  const [pagination, setPagination] = useState({
    pageIndex: pageParam || 1,
    pageSize: DEFAULT_PAGE_SIZE,
  })

  const { data, isLoading, isError } = useGetQuotingProspects({
    'page[number]': pagination.pageIndex,
    'page[size]': pagination.pageSize,
    ...(currentUserId && { owner_id: currentUserId }),
    ...(currentPartnerId && { partner_id: currentPartnerId }),
  })

  const table = useReactTable({
    data: data?.data || [],
    columns,
    manualPagination: true,
    getCoreRowModel: getCoreRowModel(),
    state: {
      columnVisibility: {
        name: true,
        email: true,
        created_at: true,
      },
      pagination,
    },
  })

  useEffect(() => {
    if (data) {
      setPaginationMetadata(data?.pagination)
    }
  }, [data?.pagination])

  useEffect(() => {
    const pageURL = new URL(window.location.href)
    const pageIndex = pagination.pageIndex.toString()
    pageURL.searchParams.set('page', pageIndex)
    window.history.pushState({}, '', pageURL.toString())
  }, [pagination.pageIndex])

  useEffect(() => {
    // `quote_submitted` is only used to flash a success message from QuotesController
    // so we remove it from the URL after it's detected
    const url = new URL(window.location.href)
    url.searchParams.delete('quote_submitted')
    window.history.replaceState({}, document.title, url)
  }, [])

  return (
    <>
      <div className='flex flex-row items-start justify-between flex-wrap gap-3 mb-6'>
        <h1 className='heading-lg'>Quotes</h1>
        {canOwnProspects && (
          <RequestAQuoteButton
            location='top_nav'
          />
        )}
      </div>
      <TanstackTable
        table={table}
        isLoading={isLoading}
        isError={isError}
        noDataMessage={
          <div className='flex flex-col items-center justify-center gap-4'>
            You haven't requested any quotes yet.
            {canOwnProspects && (
              <RequestAQuoteButton
                isPrimary={false}
                location='quotes_zero_state'
              />
            )}
          </div>
        }
        onRowClick={undefined}
        paginationLabelSingular='quote'
        paginationLabelPlural='quotes'
        paginationMetadata={paginationMetadata}
        onNextPageClick={() =>
          setPagination({
            ...pagination,
            pageIndex: paginationMetadata?.next_page || 0,
          })}
        onPreviousPageClick={() =>
          setPagination({
            ...pagination,
            pageIndex: paginationMetadata?.prev_page || 0,
          })}
      />
    </>
  )
}

const PartnersQuotesList = ({
  currentUserId,
  currentPartnerId,
  canOwnProspects,
  pageParam,
}: PartnersQuotesListProps) => {
  return (
    <PartnersQuotesListImpl
      currentUserId={currentUserId}
      currentPartnerId={currentPartnerId}
      canOwnProspects={canOwnProspects}
      pageParam={pageParam}
    />
  )
}

export default PartnersQuotesList
